import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Link,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import BackButton from "../../components/common/BackButton";
import Loader from "../../components/common/Loader";
import { SERVER_ORIGIN } from "../../utilities/constants";
import { hasAdminAccess,getDashboardPath } from "../../utilities/helper_functions";
import css from "../../css/admin/users-page.module.css";

const getAdminAuthHeaders = (omitContentType = false) => {
  const headers = {
    "auth-token": sessionStorage.getItem("token"),
  };
  if (!omitContentType) headers["Content-Type"] = "application/json";
  const adminId = process.env.REACT_APP_ADMIN_ID || "yuva@gmail.com";
  const adminPassword = process.env.REACT_APP_ADMIN_PASSWORD || "admin@yuva123";
  headers.Authorization = `Basic ${btoa(`${adminId}:${adminPassword}`)}`;
  return headers;
};

const formatDate = (d) => {
  if (!d) return "-";
  const date = new Date(d);
  return isNaN(date.getTime()) ? "-" : date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const HistoricalMoUPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [list, setList] = useState([]);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [uploadForm, setUploadForm] = useState({
    signDate: new Date().toISOString().slice(0, 10),
    mouDuration: "1",
    document_file: null,
  });
  const hasAdmin = hasAdminAccess();

  useEffect(() => {
    if (!hasAdmin) {
      navigate("/admin/services");
      return;
    }
    fetchHistoricalMoU();
  }, [hasAdmin, navigate]);

  const fetchHistoricalMoU = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${SERVER_ORIGIN}/api/admin/auth/historical-mou`, {
        method: "GET",
        headers: getAdminAuthHeaders(),
      });
      const result = await res.json();
      if (res.ok && result.data?.list) setList(result.data.list);
      else setList([]);
    } catch (err) {
      console.error("Historical MoU fetch error:", err);
      setList([]);
      setMessage({ type: "error", text: "Failed to load historical MoU data." });
    }
    setLoading(false);
  };

  const openUpload = (row) => {
    setSelectedRow(row);
    setUploadForm({
      signDate: row.signDate ? new Date(row.signDate).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10),
      mouDuration: row.mouDuration || "1",
      document_file: null,
    });
    setUploadOpen(true);
  };

  const closeUpload = () => {
    setUploadOpen(false);
    setSelectedRow(null);
    setUploadForm({ signDate: "", mouDuration: "1", document_file: null });
  };

  const handleUploadSubmit = async () => {
    if (!selectedRow?.instituteId || !uploadForm.document_file) {
      setMessage({ type: "error", text: "Please select a PDF file." });
      return;
    }
    setUploading(true);
    setMessage({ type: "", text: "" });
    try {
      const formData = new FormData();
      formData.append("institute_id", selectedRow.instituteId);
      formData.append("signDate", uploadForm.signDate || new Date().toISOString().slice(0, 10));
      formData.append("mouDuration", uploadForm.mouDuration || "1");
      formData.append("document_file", uploadForm.document_file);
      const res = await fetch(`${SERVER_ORIGIN}/api/admin/auth/upload-mou-signed-pdf`, {
        method: "POST",
        headers: getAdminAuthHeaders(true),
        body: formData,
      });
      const result = await res.json();
      if (res.ok) {
        setMessage({ type: "success", text: result.statusText || "Signed PDF uploaded successfully." });
        closeUpload();
        fetchHistoricalMoU();
      } else {
        setMessage({ type: "error", text: result.statusText || "Upload failed." });
      }
    } catch (err) {
      setMessage({ type: "error", text: "Upload failed." });
    }
    setUploading(false);
  };

  if (!hasAdmin) return null;

  return (
    <div className={css.outerDiv}>
       <div className={css.headerSection}>
              <div className={css.headerLeft}>
                <BackButton
                  text="Back to Dashboard"
                  customPath={getDashboardPath()}
                />
              </div>
            </div>
      {/* <BackButton onClick={() => navigate("/admin/services")} /> */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h5" sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <DescriptionIcon /> Historical MoU & Acceptance Data
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View past MoU documents with EM and Institute details. Update acceptance data by uploading a new signed PDF for any record.
          </Typography>
        </CardContent>
      </Card>

      {message.text && (
        <Alert severity={message.type === "success" ? "success" : "error"} sx={{ mb: 2 }} onClose={() => setMessage({ type: "", text: "" })}>
          {message.text}
        </Alert>
      )}

      <Card>
        <CardContent>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <Loader />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Institute</TableCell>
                    <TableCell>Officer / Contact</TableCell>
                    <TableCell>EM</TableCell>
                    <TableCell>Chapter / Region</TableCell>
                    <TableCell>Sign Date</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>MoU / Doc</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {list.map((row, idx) => (
                    <TableRow key={`${row.instituteId}-${row.signDate}-${idx}`}>
                      <TableCell>
                        <strong>{row.instituteName}</strong>
                      </TableCell>
                      <TableCell>
                        {row.officerName} / {row.officerEmail}
                      </TableCell>
                      <TableCell>
                        {row.emName || "-"} ({row.emEmail || "-"})
                      </TableCell>
                      <TableCell>
                        {row.chapterName || "-"} / {row.region || "-"}
                      </TableCell>
                      <TableCell>{formatDate(row.signDate)}</TableCell>
                      <TableCell>{row.mouDuration || "-"} yr</TableCell>
                      <TableCell>
                        {row.mouUrl ? (
                          <Link href={row.mouUrl} target="_blank" rel="noopener noreferrer">
                            View PDF
                          </Link>
                        ) : (
                          row.docUrl ? (
                            <Link href={row.docUrl} target="_blank" rel="noopener noreferrer">Current doc</Link>
                          ) : "-"
                        )}
                      </TableCell>
                      <TableCell>
                        <Button size="small" variant="outlined" startIcon={<UploadFileIcon />} onClick={() => openUpload(row)}>
                          Update signed PDF
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {list.length === 0 && (
                <Typography color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
                  No historical MoU records found.
                </Typography>
              )}
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={uploadOpen} onClose={closeUpload} maxWidth="sm" fullWidth>
        <DialogTitle>Upload / Update signed MoU PDF</DialogTitle>
        <DialogContent>
          {selectedRow && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Institute: <strong>{selectedRow.instituteName}</strong> · EM: {selectedRow.emName}
              </Typography>
              <TextField
                fullWidth
                margin="dense"
                type="date"
                label="Sign Date"
                InputLabelProps={{ shrink: true }}
                value={uploadForm.signDate}
                onChange={(e) => setUploadForm((f) => ({ ...f, signDate: e.target.value }))}
              />
              <TextField
                fullWidth
                margin="dense"
                label="MoU duration (years)"
                value={uploadForm.mouDuration}
                onChange={(e) => setUploadForm((f) => ({ ...f, mouDuration: e.target.value }))}
              />
              <Button variant="outlined" component="label" sx={{ mt: 2 }}>
                Choose PDF file
                <input
                  type="file"
                  hidden
                  accept=".pdf"
                  onChange={(e) => setUploadForm((f) => ({ ...f, document_file: e.target.files?.[0] || null }))}
                />
              </Button>
              {uploadForm.document_file && (
                <Typography variant="body2" sx={{ mt: 1 }}>{uploadForm.document_file.name}</Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeUpload}>Cancel</Button>
          <Button variant="contained" onClick={handleUploadSubmit} disabled={uploading || !uploadForm.document_file}>
            {uploading ? "Uploading…" : "Upload"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default HistoricalMoUPage;
